type PageLoader = () => Promise<unknown>

// Warms every page chunk during idle time so subsequent Inertia navigations
// resolve from cache instead of blocking on a network fetch. We only do this
// once per deploy: the localStorage key is suffixed with the Inertia asset
// `version`, so a new deploy (new version) re-warms the cache exactly once.
//
// No-op in development, where Vite serves pages as unbundled modules on demand.
export function preloadPageChunks (pages: Record<string, unknown>, version: string | null | undefined) {
  if (import.meta.env.DEV || import.meta.env.SSR || typeof window === 'undefined') return

  const storageKey = `preload-page-chunks:${version ?? 'unknown'}`

  try {
    if (localStorage.getItem(storageKey)) return
  }
  catch {
    // localStorage can throw (private mode, storage disabled); skip preloading.
    return
  }

  const loaders = Object.values(pages).filter(
    (page): page is PageLoader => typeof page === 'function',
  )
  if (!loaders.length) return

  const preload = () => {
    Promise.allSettled(loaders.map(load => load())).then(() => {
      try {
        localStorage.setItem(storageKey, '1')
      }
      catch {
        // Ignore write failures — we simply re-attempt the warm on the next load.
      }
    })
  }

  if (typeof window.requestIdleCallback === 'function')
    window.requestIdleCallback(preload, { timeout: 4000 })
  else
    setTimeout(preload, 1500)
}
